import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { StoreRegion } from '@appchoose/contact';
import { ErrorBoundary } from '@sentry/react';
import { createFileRoute } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { selectedMarketingCampaignState } from '~/atoms/calendar/selectedMarketingCampaignState';
import { ImmersiveSale } from '~/components/common/immersive-sale/immersive-sale';
import type { CalendarRef } from '~/components/common/marketing-calendar/calendar';
import { Calendar } from '~/components/common/marketing-calendar/calendar';
import { CalendarHeader } from '~/components/common/marketing-calendar/calendar-header';
import {
  Sidebar,
  SidebarContent,
  SidebarTrigger,
} from '~/components/ui/sidebar';
import { withAuthorizationRequired } from '~/libs/auth';
import { AppLoader } from '~/routes/_auth';
import { titleWrapper } from '~/utils/title';
import { marketingQueryParamsSchema } from '~/validators/marketing-validator';

export type ReferralMarketingForm = {
  bigImage: string;
  saleId: string;
  smallImage: string;
};

export const MARKETING_SCOPES = [
  'scope.meusnidus.notifications.view',
  'scope.meusnidus.notifications.write',
];

export const Route = createFileRoute('/_auth/_main-navigation/marketing/')({
  component: withAuthorizationRequired(RouteComponent, {
    onRedirecting: AppLoader,
    requireScope: MARKETING_SCOPES,
  }),
  validateSearch: (search) => marketingQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();

  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();

  const calendarRef = useRef<CalendarRef | null>(null);

  const isImmersiveSaleDetailsOpen = searchParams.edit === true;

  const [_, setSelectedMarketingCampaignState] = useAtom(
    selectedMarketingCampaignState,
  );

  const onPressAddImmersiveSale = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        edit: true,
      }),
      resetScroll: false,
    });
  };

  const closeImmersiveSaleDetails = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        edit: false,
        immersiveId: undefined,
      }),
      resetScroll: false,
    });
    setSelectedMarketingCampaignState(undefined);
  };

  const onChangeRegion = (region: StoreRegion) => {
    navigate({
      search: (prev) => ({ ...prev, region }),
    });
  };

  return (
    <>
      <title>{titleWrapper(t('marketing_page.title'))}</title>
      <section
        aria-labelledby="primary-heading"
        className="flex h-screen w-full flex-col pt-4 sm:pt-14 lg:mr-[32rem] xl:px-14"
      >
        <CalendarHeader
          onPressAddImmersiveSale={onPressAddImmersiveSale}
          onChangeRegion={onChangeRegion}
          region={searchParams.region}
          calendarRef={calendarRef}
        />
        <ErrorBoundary
          fallback={
            <div className="flex size-full items-center justify-center">
              {t('calendar.error')}
            </div>
          }
        >
          <Calendar calendarRef={calendarRef} />
        </ErrorBoundary>
        <Sidebar
          open={isImmersiveSaleDetailsOpen}
          onOpenChange={closeImmersiveSaleDetails}
          modal={false}
        >
          {/* Sidebar overlay when modal={false} */}
          {isImmersiveSaleDetailsOpen && (
            <SidebarTrigger
              className="fixed inset-0 z-40 bg-black/30 data-[state=closed]:duration-300 data-[state=open]:duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              data-state="open"
            />
          )}
          <SidebarContent
            className="max-w-5xl lg:w-3/4"
            // Disable overflow on body when the sidebar is open
            data-no-modal={true}
            // Prevent the sidebar from closing when clicking outside (e.g. on a Chrome extension)
            onPointerDownOutside={(e) => e.preventDefault()}
            onFocusOutside={(e) => e.preventDefault()}
          >
            <ImmersiveSale
              setIsOpen={() => {
                closeImmersiveSaleDetails();
              }}
            />
          </SidebarContent>
        </Sidebar>
      </section>
    </>
  );
}
